import type { FC, MouseEventHandler } from "react";

import { getBuilderImageResizeUrl } from "@ui/utils/getBuilderImageResizeUrl";
import { getOptimizedBackgroundImage } from "@ui/utils/getOptimizedBackgroundImage";
import classNames from "classnames";

import type { DirectionalIconComponent } from "@jti/brand-icons";
import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import { LinkBehaviour } from "@jti/core-components/components/LinkBehaviour";

import styles from "./RedirectionLinkCard.module.scss";

type DefaultCardVariants = "list" | "ploomClub" | "withImage";

type ButtonCardVariant = "button";

type RedirectionLinkCardPropsBase = {
  title: string;
  image?: string;
  trackingEvent?: "phone_click" | "contact_click" | "store_locator_click";
  dataTestid?: string;
  ariaPressed?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
} & Partial<Pick<HTMLElement, "className">>;

type WithArrowHidden = {
  isArrowHidden?: boolean;
  iconElement?: never;
};

type WithIconElement = {
  isArrowHidden?: true;
  iconElement: DirectionalIconComponent;
};

type WithDefaultVariants = {
  variant?: DefaultCardVariants;
  link: string;
};

type WithButtonVariant = {
  variant: ButtonCardVariant;
  link?: never;
};

export type RedirectionLinkCardProps = RedirectionLinkCardPropsBase &
  (WithArrowHidden | WithIconElement) &
  (WithDefaultVariants | WithButtonVariant);

export const RedirectionLinkCard: FC<RedirectionLinkCardProps> = ({
  image,
  title,
  onClick,
  variant = "list",
  className,
  isArrowHidden,
  iconElement: IconElement,
  dataTestid,
  ariaPressed,
  link,
}) => {
  const isListVariant = variant === "list";
  const isButtonVariant = variant === "button";
  const shouldShowArrow = (isListVariant || isButtonVariant) && !isArrowHidden && !IconElement;
  const builderResizedImage = getBuilderImageResizeUrl({ imageUrl: image, width: 40, height: 40 });
  const optimizedImage = builderResizedImage ? getOptimizedBackgroundImage(builderResizedImage, 40, 40) : undefined;
  const RootTag = isButtonVariant ? "button" : LinkBehaviour;
  const rootProps = isButtonVariant ? { "aria-pressed": ariaPressed } : { href: link };

  return (
    <RootTag
      className={classNames(styles.redirectionCardLink, className, styles.contentWrapper, {
        [styles.ploomClub]: variant === "ploomClub",
        [styles.list]: isListVariant,
      })}
      data-testid={dataTestid}
      onClick={onClick}
      {...rootProps}
    >
      <span className={styles.title}>{title}</span>

      {IconElement && <IconElement className={styles.icon} data-testid="icon-element" />}

      {shouldShowArrow && (
        <LongArrowForwardIcon
          className={classNames(styles.icon, styles.arrowIcon)}
          direction="ltr"
          data-testid="icon-chevron-right"
        />
      )}
      {variant === "withImage" && image && (
        <div
          className={styles.imageContainer}
          data-testid="redirection-link-card-image"
          style={{
            "--image-url": optimizedImage,
          }}
        ></div>
      )}
    </RootTag>
  );
};
